import { Fragment, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { RequirePermission } from '@/components/RequirePermission';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import type { SellerPortalUser } from '@/api/types';

/**
 * ADR-076 Part C - seller portal user management, scoped to ONE seller
 * (owner). The list endpoint itself is org-wide (it has to be: a portal
 * user can span more than one owner - ADR-075's own seller_membership
 * design), so this panel filters client-side to the owner whose detail
 * page it is embedded in. Only ever reads state/email/display_name/
 * timestamps/counts off the list/audit responses - those never carry a
 * credential value.
 *
 * SETUP-LINK FALLBACK: the ONE exception is `setupLink` local state below,
 * populated directly from an invite/resend-invite call's OWN response body
 * (endpoints.ts's `setupUrl`, present only when office.ts decided no real
 * email provider is configured). It lives in this component's state alone -
 * never written to storage, never refetched, gone on unmount/refresh/
 * another invite - matching the one-shot guarantee the backend already
 * enforces (a page reload cannot recover it; only a fresh invite/resend
 * call can, which mints a genuinely new token).
 */
export function PortalUsersPanel({ ownerId, openInvite = false, onChanged }: { ownerId: string; openInvite?: boolean; onChanged?: () => void }): JSX.Element {
  const result = useApi(() => endpoints.sellerPortalUsers(), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [auditFor, setAuditFor] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(openInvite);
  const [setupLink, setSetupLink] = useState<{ email: string; url: string } | null>(null);
  const { t, locale } = useLanguage();

  async function run(id: string, fn: () => Promise<void>): Promise<void> {
    setBusyId(id);
    setActionError(null);
    try {
      await fn();
      result.reload();
      onChanged?.();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusyId(null);
    }
  }

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const rows = result.data.sellerUsers.filter((u) => u.owner_ids.includes(ownerId));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>{t('portalUsers.title')}</h3>
        <RequirePermission permission="USERS.WRITE">
          <button type="button" onClick={() => setShowInvite((v) => !v)}>
            {showInvite ? t('common.cancel') : t('portalUsers.inviteUser')}
          </button>
        </RequirePermission>
      </div>

      {Boolean(actionError) && <ErrorBanner error={actionError} onRetry={() => setActionError(null)} />}

      {showInvite && (
        <form
          style={{ marginTop: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            const email = String(form.get('email'));
            void run('invite', async () => {
              const res = await endpoints.inviteSellerPortalUser({
                email,
                displayName: String(form.get('displayName')),
                ownerIds: [ownerId],
              });
              setShowInvite(false);
              setSetupLink(res.setupUrl ? { email, url: res.setupUrl } : null);
            });
          }}
        >
          <fieldset disabled={busyId === 'invite'}>
            <label>
              {t('users.email')} <input type="email" name="email" required />
            </label>
            <label>
              {t('portalUsers.displayName')} <input name="displayName" required />
            </label>
            <button type="submit">{t('portalUsers.sendInvite')}</button>
          </fieldset>
          <p className="disabled-note" style={{ marginTop: 8 }}>
            {t('portalUsers.noEmailProviderNote')}
          </p>
        </form>
      )}

      {setupLink && <SetupLinkBanner email={setupLink.email} url={setupLink.url} onDismiss={() => setSetupLink(null)} />}

      {rows.length === 0 ? (
        <EmptyState title={t('portalUsers.emptyTitle')} />
      ) : (
        <table style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>{t('users.email')}</th>
              <th>{t('onboarding.name')}</th>
              <th>{t('common.status')}</th>
              <th>{t('portalUsers.lastLogin')}</th>
              <th>{t('portalUsers.activeSessions')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u: SellerPortalUser) => (
              <Fragment key={u.id}>
                <tr>
                  <td>{u.email}</td>
                  <td>{u.display_name}</td>
                  <td>
                    <StatusBadge value={u.state} />
                  </td>
                  <td>{u.last_login_at ? formatDateTime(u.last_login_at, locale) : t('channels.never')}</td>
                  <td>{formatNumber(u.active_sessions, locale)}</td>
                  <td>
                    <RequirePermission permission="USERS.WRITE">
                      {u.state === 'PENDING' && (
                        <button
                          type="button"
                          disabled={busyId === u.id}
                          onClick={() =>
                            void run(u.id, async () => {
                              const res = await endpoints.resendSellerPortalInvite(u.id);
                              setSetupLink(res.setupUrl ? { email: u.email, url: res.setupUrl } : null);
                            })
                          }
                        >
                          {t('portalUsers.resendInvite')}
                        </button>
                      )}
                      {u.state === 'ACTIVE' && (
                        <button type="button" disabled={busyId === u.id} onClick={() => void run(u.id, async () => { await endpoints.triggerSellerPortalReset(u.id); })}>
                          {t('portalUsers.sendPasswordReset')}
                        </button>
                      )}
                      {u.state !== 'DISABLED' ? (
                        <button
                          type="button"
                          disabled={busyId === u.id}
                          onClick={() => void run(u.id, async () => { await endpoints.setSellerPortalUserState({ sellerUserId: u.id, state: 'DISABLED' }); })}
                        >
                          {t('portalUsers.disable')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busyId === u.id}
                          onClick={() => void run(u.id, async () => { await endpoints.setSellerPortalUserState({ sellerUserId: u.id, state: 'ACTIVE' }); })}
                        >
                          {t('portalUsers.enable')}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busyId === u.id || Number(u.active_sessions) === 0}
                        onClick={() => void run(u.id, async () => { await endpoints.revokeSellerPortalSessions(u.id); })}
                      >
                        {t('portalUsers.revokeSessions')}
                      </button>
                    </RequirePermission>
                    <button type="button" onClick={() => setAuditFor(auditFor === u.id ? null : u.id)}>
                      {auditFor === u.id ? t('portalUsers.hideAudit') : t('portalUsers.viewAudit')}
                    </button>
                  </td>
                </tr>
                {auditFor === u.id && (
                  <tr>
                    <td colSpan={6}>
                      <AuditTrail sellerUserId={u.id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * The one-time setup link, shown exactly once as the direct result of an
 * invite/resend-invite call - see PortalUsersPanel's own `setupLink` state
 * comment. Never fetched, never re-shown after a refresh; only dismissible
 * or replaced by the next invite/resend response.
 */
function SetupLinkBanner({ email, url, onDismiss }: { email: string; url: string; onDismiss: () => void }): JSX.Element {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="empty-state" role="status" style={{ marginTop: 12 }} data-testid="setup-link-banner">
      <p style={{ fontWeight: 600, marginBottom: 4 }}>{t('portalUsers.setupLinkFor', { email })}</p>
      <p className="sub" style={{ marginBottom: 8 }}>
        {t('portalUsers.setupLinkWarning')}
      </p>
      <input
        type="text"
        readOnly
        value={url}
        onFocus={(e) => e.currentTarget.select()}
        style={{ width: '100%', marginBottom: 8 }}
        data-testid="setup-link-input"
      />
      <button type="button" onClick={() => void copy()}>
        {copied ? t('portalUsers.setupLinkCopied') : t('portalUsers.setupLinkCopy')}
      </button>{' '}
      <button type="button" onClick={onDismiss}>
        {t('common.cancel')}
      </button>
    </div>
  );
}

function AuditTrail({ sellerUserId }: { sellerUserId: string }): JSX.Element {
  const result = useApi(() => endpoints.sellerPortalUserAudit(sellerUserId), [sellerUserId]);
  const { t, locale } = useLanguage();
  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;
  if (result.data.events.length === 0) return <EmptyState title={t('portalUsers.noAuditEvents')} />;
  return (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {result.data.events.map((e) => (
        <li key={e.id}>
          <span className="sub">{formatDateTime(e.occurred_at, locale)}</span> — {e.action} ({e.actor_kind})
        </li>
      ))}
    </ul>
  );
}
